import React, { useState } from 'react';
import { Play, Check, Terminal, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { skills } from '../mock';
import { useTheme } from '../context/ThemeContext';

const Skills = () => {
  // Use object to track each cell's state independently
  const [cellStates, setCellStates] = useState({});
  const { theme } = useTheme();

  const handleRunCell = (cellId) => {
    setCellStates(prev => ({
      ...prev,
      [cellId]: { running: true, completed: false }
    }));
    
    setTimeout(() => {
      setCellStates(prev => ({
        ...prev,
        [cellId]: { running: false, completed: true }
      }));
    }, 1500);
  };

  const toggleCell = (cellId) => {
    setCellStates(prev => ({
      ...prev,
      [cellId]: { 
        ...prev[cellId],
        completed: !prev[cellId]?.completed 
      }
    }));
  };

  const getCellState = (cellId) => ({
    running: cellStates[cellId]?.running || false,
    completed: cellStates[cellId]?.completed || false
  });

  const categories = [
    ...new Set(skills.technical.map((skill) => skill.category))
  ];

  return (
    <section id="skills" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#0f1f26] to-[#1B3139]' 
        : 'bg-gradient-to-br from-gray-100 to-white'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Terminal className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Technical Expertise
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Skills & Technologies
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Run cells to explore my technical skill set and expertise across various domains
          </p>
        </div>

        {/* Professional Skills Cell */}
        <div className="mb-6 sm:mb-8">
          <div className={`border rounded-lg overflow-hidden shadow-xl ${
            theme === 'dark' 
              ? 'bg-[#1B3139] border-white/10' 
              : 'bg-white border-gray-200'
          }`}>
            <div className={`flex items-center justify-between px-3 sm:px-4 py-2 border-b ${
              theme === 'dark' 
                ? 'bg-white/5 border-white/10' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <span 
                className={`text-xs font-mono truncate ${
                  theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                }`}
                style={{ fontFamily: 'DM Mono, monospace' }}
              >
                Cell [2]: Professional Skills
              </span>
              {getCellState('professional').completed && (
                <button 
                  onClick={() => toggleCell('professional')}
                  className={`p-1 rounded transition-colors ${
                    theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                  }`}
                >
                  <ChevronUp className={`h-4 w-4 ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`} />
                </button>
              )}
            </div>

            <div className="relative">
              <div className={`absolute left-0 top-0 bottom-0 w-10 sm:w-12 border-r flex items-start justify-center pt-4 ${
                theme === 'dark' 
                  ? 'bg-white/5 border-white/10' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <Button
                  onClick={() => handleRunCell('professional')}
                  disabled={getCellState('professional').running}
                  className="bg-transparent hover:bg-[#FF3621]/20 border border-[#FF3621]/50 text-[#FF3621] p-1.5 sm:p-2 h-7 w-7 sm:h-8 sm:w-8 rounded transition-all duration-300"
                >
                  {getCellState('professional').running ? (
                    <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-[#FF3621] border-t-transparent rounded-full"></div>
                  ) : getCellState('professional').completed ? (
                    <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                  ) : (
                    <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                </Button>
              </div>

              <div className="pl-12 sm:pl-16 pr-3 sm:pr-4 py-3 sm:py-4">
                <pre 
                  className={`text-xs sm:text-sm font-mono mb-4 overflow-x-auto ${
                    theme === 'dark' ? 'text-white/80' : 'text-gray-700'
                  }`}
                  style={{ fontFamily: 'DM Mono, monospace' }}
                >
                  professional_skills = [<br />
                  {skills.professional.slice(0, 3).map((skill, idx) => (
                    <span key={idx}>    "{skill}",<br /></span>
                  ))}
                  <span className={theme === 'dark' ? 'text-white/40' : 'text-gray-400'}>    # ... {skills.professional.length - 3} more</span>
                  <br />]
                </pre>
              </div>
            </div>

            {getCellState('professional').completed && (
              <div className={`border-t px-3 sm:px-4 py-3 sm:py-4 ${
                theme === 'dark' 
                  ? 'border-white/10 bg-black/30' 
                  : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {skills.professional.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-2 sm:px-3 py-1 bg-[#FF3621]/10 border border-[#FF3621]/30 text-[#FF3621] rounded-full text-xs sm:text-sm font-medium"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Technical Skills by Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {categories.slice(0, 6).map((category, idx) => {
            const categorySkills = skills.technical.filter(
              (skill) => skill.category === category
            );
            const cellId = `tech-${category}`;
            const state = getCellState(cellId);

            return (
              <div key={category} className={`border rounded-lg overflow-hidden shadow-xl ${
                theme === 'dark' 
                  ? 'bg-[#1B3139] border-white/10' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className={`flex items-center justify-between px-3 sm:px-4 py-2 border-b ${
                  theme === 'dark' 
                    ? 'bg-white/5 border-white/10' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <span 
                    className={`text-xs font-mono truncate ${
                      theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                    }`}
                    style={{ fontFamily: 'DM Mono, monospace' }}
                  >
                    Cell [{idx + 3}]: {category}
                  </span>
                  {state.completed && (
                    <button 
                      onClick={() => toggleCell(cellId)}
                      className={`p-1 rounded transition-colors ${
                        theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                      }`}
                    >
                      <ChevronUp className={`h-4 w-4 ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`} />
                    </button>
                  )}
                </div>

                <div className="relative">
                  <div className={`absolute left-0 top-0 bottom-0 w-10 sm:w-12 border-r flex items-start justify-center pt-4 ${
                    theme === 'dark' 
                      ? 'bg-white/5 border-white/10' 
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <Button
                      onClick={() => handleRunCell(cellId)}
                      disabled={state.running}
                      className="bg-transparent hover:bg-[#FF3621]/20 border border-[#FF3621]/50 text-[#FF3621] p-1.5 sm:p-2 h-7 w-7 sm:h-8 sm:w-8 rounded transition-all duration-300"
                    >
                      {state.running ? (
                        <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-[#FF3621] border-t-transparent rounded-full"></div>
                      ) : state.completed ? (
                        <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                      ) : (
                        <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                    </Button>
                  </div>

                  <div className="pl-12 sm:pl-16 pr-3 sm:pr-4 py-3 sm:py-4">
                    <pre 
                      className={`text-xs sm:text-sm font-mono overflow-x-auto ${
                        theme === 'dark' ? 'text-white/80' : 'text-gray-700'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      {category.toLowerCase().replace(/\s+/g, '_')}_skills = [<br />
                      {categorySkills.slice(0, 2).map((skill, i) => (
                        <span key={i}>    "{skill.name}",<br /></span>
                      ))}
                      ]
                    </pre>
                  </div>
                </div>

                {state.completed && (
                  <div className={`border-t px-3 sm:px-4 py-3 sm:py-4 ${
                    theme === 'dark' 
                      ? 'border-white/10 bg-black/30' 
                      : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {categorySkills.map((skill, i) => (
                        <span
                          key={i}
                          className="px-2 sm:px-3 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded text-xs sm:text-sm font-medium"
                          style={{ fontFamily: 'DM Sans, sans-serif' }}
                        >
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;
